import { Button, Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import { setTriggerCargoSearch, setSearchResultTotalRecord, resetCargoSearch, setContactAccountNames, setCargoContactSearchKeyword, setCharterers, setCargoTypes, setCargoGroups, setDwtMin, setDwtMax, setLoadPortAreas, setDisportAreas, setWithinDays, setCountries, setSearchType, setTriggerFocusCharterer, setSearchFixed, setIncludeOutdatedCargoes, setOrderDetail, setCharterersForInput } from './reducers/CargoSearchReducer';
import CargoApi, { SearchCargoParam } from './api/CargoApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch } from '../SavedSearch/reducers/SavedSearchReducer';
import { cargoSearchSelector, setCargoSearchResults, setDateFrom, setDateTo } from './reducers/CargoSearchReducer';
import CargoModel from './models/CargoModel';
import TagsFilter from '../TagFilter/TagsFilter';
import { searchContactName } from '../ContactSelector/ContactSelector';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import CargoTypeMultipleSelector from '../CargoTypeSelector/CargoTypeMultipleSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import CargoGroupModel from '../CargoTypeSelector/models/CargoGroupModel';
import DwtSelector from '../Tonnage/components/DwtSelector';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import AreaModel from '../PortSelector/models/AreaModel';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import { CountryModel } from '../Contact/models/ContactStaticResourcesModel';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import { SearchType } from '../Tonnage/reducers/TonnageSearchReducer';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';


export const cargoTypesToQueryItems = (cargoTypes: CargoTypeModel[]) => {
    return _.map(cargoTypes, (cargoType) => {
        return {
            id: cargoType.id.toString(),
            value: cargoType.name,
            positive: cargoType.positive
        } as QueryItem
    })
}

export const queryItemsToCargoTypes = (queryItems: QueryItem[]) => {
    return _.map(queryItems, (queryItem) => {
        return {
            id: parseInt(queryItem.id),
            name: queryItem.value,
            positive: queryItem.positive
        } as CargoTypeModel
    })
}


const CargoSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const cargoSearchData = useSelector(cargoSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (cargoSearchData.contactSearchKeyword && cargoSearchData.contactSearchKeyword.length > 0) {
                searchContactName(cargoSearchData.contactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setContactAccountNames(contacts));
                    } else {
                        dispatch(setContactAccountNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [cargoSearchData.contactSearchKeyword])

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Cargo,
                    queryJson: JSON.stringify(cargoSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (cargoSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        CargoApi.Search({
            query: cargoSearchData.query
        } as SearchCargoParam)
            .then((response: BaseResponse<CargoModel[]>) => {
                if (response.success) {
                    dispatch(setCargoSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                    dispatch(setSearchType(SearchType.Search));
                    dispatch(setCharterersForInput(""));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [cargoSearchData.triggerSearch])

    const cargoTypesToQueryItems = (cargoTypes: CargoTypeModel[]) => {
        return _.map(cargoTypes, (cargoType) => {
            return {
                id: cargoType.id.toString(),
                value: cargoType.name,
                positive: cargoType.positive
            } as QueryItem
        })
    }

    const queryItemsToCargoTypes = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                id: parseInt(queryItem.id),
                name: queryItem.value,
                positive: queryItem.positive
            } as CargoTypeModel
        })
    }

    const cargoGroupsToQueryItems = (cargoGroups: CargoGroupModel[]) => {
        return _.map(cargoGroups, (cargoGroup) => {
            return {
                id: cargoGroup.cargoGroupName,
                value: cargoGroup.cargoGroupName,
                positive: cargoGroup.positive
            } as QueryItem
        })
    }

    const queryItemsToCargoGroups = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                cargoGroupName: queryItem.value,
                positive: queryItem.positive
            } as CargoGroupModel
        })
    }

    const areasToQueryItems = (areas: AreaModel[]) => {
        return _.map(areas, (area) => {
            return {
                id: area.name,
                value: area.name,
                positive: area.positive
            } as QueryItem
        })
    }

    const queryItemsToAeras = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                name: queryItem.value,
                positive: queryItem.positive
            } as AreaModel
        })
    }

    const countryToQueryItems = (countries: CountryModel[]) => {
        return _.map(countries, (country) => {
            return {
                id: country.code,
                value: country.code,
                positive: true
            } as QueryItem
        })
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Cargo}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerCargoSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetCargoSearch());
                                                dispatch(setTriggerFocusCharterer((new Date()).getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(cargoSearchData.contactAccountNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Account Name"
                        onInputChange={(text: string) => {
                            dispatch(setCargoContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={cargoSearchData.query.charterers}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCharterers(value))
                        }}
                        triggerFocusInput={cargoSearchData.triggerFocusCharterer}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(cargoTypeSelectorData.cargoTypes, (item) => {
                            return {
                                id: item.name,
                                value: item.name,
                                positive: true
                            } as QueryItem
                        })}
                        label="Cargo"
                        useFreeText={true}
                        className="tonnage-search-item"
                        value={cargoSearchData.query.cargoTypes}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCargoTypes(value))
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(cargoTypeSelectorData.cargoGroups, (item) => {
                            return {
                                id: item.cargoGroupName,
                                value: item.cargoGroupName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Cargo Group"
                        useFreeText={true}
                        className="tonnage-search-item"
                        value={cargoSearchData.query.cargoGroups}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCargoGroups(value))
                        }}
                    ></TagsFilter>
                    <DwtSelector
                        dwtFromValue={cargoSearchData.query.dwtMin}
                        dwtToValue={cargoSearchData.query.dwtMax}
                        className="tonnage-search-item"
                        dwtFromLabel='Dwt Min(T)'
                        dwtToLabel='Dwt Max(T)'
                        useSizeSelector={true}
                        onDwtFromChange={(value: number) => {
                            dispatch(setDwtMin(value));
                        }}
                        onDwtToChange={(value: number) => {
                            dispatch(setDwtMax(value));
                        }}
                    ></DwtSelector>
                    <PortMultipleSelector
                        onAreaChange={(area: AreaModel[]) => {
                            if (area) {
                                dispatch(setLoadPortAreas(areasToQueryItems(area)));
                            }
                        }}
                        useFreeText={true}
                        areaLabel='Loadport Area'
                        areaValues={queryItemsToAeras(cargoSearchData.query.loadPortAreas)}
                        className="tonnage-search-item"
                        onlyAreaMode={true}
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <PortMultipleSelector
                        onAreaChange={(area: AreaModel[]) => {
                            if (area) {
                                dispatch(setDisportAreas(areasToQueryItems(area)));
                            }
                        }}
                        useFreeText={true}
                        areaLabel='Disport Area'
                        areaValues={queryItemsToAeras(cargoSearchData.query.disportAreas)}
                        className="tonnage-search-item"
                        onlyAreaMode={true}
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <TagsFilter
                        data={countryToQueryItems(contactInputViewData.staticResources.countries)}
                        label="Country"
                        className="tonnage-search-item"
                        value={cargoSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value))
                        }}
                    ></TagsFilter>
                    <StartEndDatepicker
                        startDateLabel="Start date"
                        endDateLabel="End date"
                        className="tonnage-search-item"
                        useQuickEndDateInput={true}
                        onStartDateChange={(date: string) => {
                            dispatch(setDateFrom(date));
                        }}
                        onEndDateChange={(date: string) => {
                            dispatch(setDateTo(date));
                        }}
                        startDateValue={cargoSearchData.query.dateFrom}
                        endDateValue={cargoSearchData.query.dateTo}
                    >
                    </StartEndDatepicker>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Sent Within"
                        type="number"
                        value={cargoSearchData.query.withinDays ? cargoSearchData.query.withinDays : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseInt(event.target.value);
                            dispatch(setWithinDays(value));
                        }}
                    >
                    </TextField>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Full Order"
                        value={cargoSearchData.query.orderDetail ? cargoSearchData.query.orderDetail : ""}
                        onChange={(event) => {
                            dispatch(setOrderDetail(event.target.value));
                        }}
                    >
                    </TextField>
                    <FormControlLabel control={
                        <Checkbox
                            checked={cargoSearchData.query.fixed}
                            onChange={(event) => {
                                dispatch(setSearchFixed(event.currentTarget.checked));
                            }}
                        />}
                        className="tonnage-input-grabs"
                        label="Include Fixed Cargo"
                    />
                    <FormControlLabel control={
                        <Checkbox
                            checked={cargoSearchData.query.includeOutdatedCargoes}
                            onChange={(event) => {
                                dispatch(setIncludeOutdatedCargoes(event.currentTarget.checked));
                            }}
                        />}
                        className="tonnage-input-grabs"
                        label="Include Outdated Cargoes"
                    />
                </div>
            </CustomAccordion>
        </>
    )
};

export default CargoSearch;